import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accounts as accountsApi, algos as algosApi } from "../api/endpoints";
import type { Account, AccountAlgoLink, Algo } from "../api/types";
import { ApiError } from "../api/client";

// Per-sleeve contract sizing for an account. One row per linked,
// non-retired algo. Each row's `contracts` cap stacks under
// account.max_contracts -- the dispatch path uses min(link.contracts,
// account.max_contracts) for the signed qty per fan-out, so a per-sleeve
// number above the account max has no effect.
interface Props {
  account: Account;
}

interface Draft {
  algoId: number;
  algoName: string;
  market: string;
  initial: number;
  current: string;
}

export function SleeveSizingCard({ account }: Props) {
  const qc = useQueryClient();
  const { data: links } = useQuery<AccountAlgoLink[]>({
    queryKey: ["account", account.id, "links"],
    queryFn: () => accountsApi.links(account.id),
  });
  const { data: algoList } = useQuery<Algo[]>({
    queryKey: ["algos"],
    queryFn: algosApi.list,
  });

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [errByAlgo, setErrByAlgo] = useState<Record<number, string | null>>({});
  const [okByAlgo, setOkByAlgo] = useState<Record<number, string | null>>({});

  // Rebuild local drafts whenever links or algos refresh upstream.
  useEffect(() => {
    if (!links || !algoList) return;
    const algoById = new Map(algoList.map((a) => [a.id, a]));
    const next: Draft[] = links
      .filter((l) => {
        const algo = algoById.get(l.algo_id);
        // hide retired algos from the editor
        return algo && !algo.retired;
      })
      .map((l) => {
        const algo = algoById.get(l.algo_id)!;
        return {
          algoId: l.algo_id,
          algoName: algo.name,
          market: algo.market,
          initial: l.contracts,
          current: String(l.contracts),
        };
      })
      .sort((a, b) => a.algoName.localeCompare(b.algoName));
    setDrafts(next);
  }, [links, algoList]);

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const n = Number(d.current);
      if (!Number.isFinite(n) || n < 1) {
        throw new Error("contracts must be 1 or higher");
      }
      if (n === d.initial) {
        throw new Error("no change");
      }
      return accountsApi.patchLink(account.id, d.algoId, { contracts: n });
    },
    onSuccess: (_data, d) => {
      setErrByAlgo((p) => ({ ...p, [d.algoId]: null }));
      setOkByAlgo((p) => ({ ...p, [d.algoId]: "saved" }));
      setTimeout(() => setOkByAlgo((p) => ({ ...p, [d.algoId]: null })), 1500);
      qc.invalidateQueries({ queryKey: ["account", account.id, "links"] });
    },
    onError: (e, d) => {
      setOkByAlgo((p) => ({ ...p, [d.algoId]: null }));
      const msg = e instanceof ApiError ? e.message : (e instanceof Error ? e.message : String(e));
      setErrByAlgo((p) => ({ ...p, [d.algoId]: msg }));
    },
  });

  const onChange = (algoId: number, value: string) => {
    setDrafts((p) => p.map((d) => (d.algoId === algoId ? { ...d, current: value } : d)));
    setErrByAlgo((p) => ({ ...p, [algoId]: null }));
  };

  return (
    <div className="card">
      <h2>Sleeve sizing</h2>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Per-sleeve contract size for this account. The account-wide
        max_contracts (<span className="mono">{account.max_contracts}</span>)
        still caps the signed qty per dispatch -- a sleeve set above the
        account max won't dispatch higher than the max.
      </div>
      {drafts.length === 0 ? (
        <div className="muted small">no linked sleeves</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Sleeve</th>
              <th>Market</th>
              <th className="num">Contracts</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {drafts.map((d) => {
              const err = errByAlgo[d.algoId];
              const ok = okByAlgo[d.algoId];
              const changed = Number(d.current) !== d.initial;
              return (
                <tr key={d.algoId}>
                  <td><span className="mono">{d.algoName}</span></td>
                  <td className="muted small">{d.market}</td>
                  <td className="num">
                    <input
                      style={{ width: 70, textAlign: "right" }}
                      value={d.current}
                      onChange={(e) => onChange(d.algoId, e.target.value)}
                      inputMode="numeric"
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => save.mutate(d)}
                      disabled={!changed || save.isPending}
                    >
                      {save.isPending ? "saving..." : "Save"}
                    </button>
                    {err && <span className="error-inline" style={{ marginLeft: 6 }}>{err}</span>}
                    {ok && <span className="muted small" style={{ marginLeft: 6, color: "var(--good, #5a5)" }}>✓ {ok}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
